const STREET_ADDRESS_REGEX = /^(\d+[A-Za-z\-]*)\s+(.*)$/;
const STATE_POSTAL_CODE_REGEX = /^([A-Za-z\s]+)\s+(\d[\dA-Za-z\-]*)$/;
const CITY_ZIP_REGEX = /^([A-Za-z\s]+)\s+([A-Z]?\d[\dA-Za-z\s]*)$/;

interface ParsedFormattedAddress {
	street: Nullable<string>;
	streetNumber: Nullable<string>;
	city: Nullable<string>;
	state: Nullable<string>;
	postalCode: Nullable<string>;
	country: Nullable<string>;
}

const _parseFormattedAddress = (formattedAddress: Nullable<string>): Nullable<ParsedFormattedAddress> => {

	if (!formattedAddress) {
		return null;
	}

	// Split formatted address by comma
	const addressArray = formattedAddress.split(',').map((_addressPart) => _addressPart.trim());

	const result: ParsedFormattedAddress = {
		street: null,
		streetNumber: null,
		city: null,
		state: null,
		postalCode: null,
		country: null,
	};

	// Get street and number
	const streetPart = addressArray[0] ?? '';

	// Try to detect street and street number
	const streetMatch = streetPart.match(STREET_ADDRESS_REGEX);

	if (streetMatch) {
		result.streetNumber = streetMatch[1];
		result.street = streetMatch[2];
	} else {
		// If no number found
		result.street = streetPart;
	}

	if (addressArray.length >= 4) {
		// Case when 4 part address
		result.city = addressArray[1];

		// Split state and postal code
		const stateZipMatch = addressArray[2].match(STATE_POSTAL_CODE_REGEX);
		if (stateZipMatch) {
			result.state = stateZipMatch[1].trim();
			result.postalCode = stateZipMatch[2].trim();
		} else {
			// Maybe no postal code or non-US format
			result.state = addressArray[2].trim();
		}

		result.country = addressArray[3].trim();
	} else if (addressArray.length === 3) {
		// Case when 3 part address
		const cityZipMatch = addressArray[1].match(CITY_ZIP_REGEX);
		if (cityZipMatch) {
			result.city = cityZipMatch[1].trim();
			result.postalCode = cityZipMatch[2].trim();
		} else {
			result.city = addressArray[1];
		}

		result.country = addressArray[2].trim();
	} else {
		// Fallback — not enough parts
		result.country = addressArray[addressArray.length - 1] ?? null;
	}

	return result;
};

export interface FormattedAddress {
	formattedAddress: string;
	city?: string;
	neighborhood?: string;
	postalCode?: string;
	country?: string;
	state?: string;
	street?: string;
	streetNumber?: string;
	aa1?: string;
	aa2?: string;
	aa3?: string;
}

export function formatAddress(address: Metadata | null): FormattedAddress {
	if (!address?.address_components) {
		return {
			formattedAddress: '',
		};
	}

	const streetObject = address.address_components.find((_ac) => (_ac.types.indexOf('route') >= 0)) ?? {};
	const cityObject = address.address_components.find((_ac) => (_ac.types.indexOf('locality') >= 0)) ?? {};
	const neighborhoodObject = address.address_components.find((_ac) => (_ac.types.indexOf('sub_locality') >= 0)) ?? {};
	const postalCodeObject = address.address_components.find((_ac) => (_ac.types.indexOf('postal_code') >= 0)) ?? {};
	const countryObject = address.address_components.find((_ac) => (_ac.types.indexOf('country') >= 0)) ?? {};
	const stateObject = address.address_components.find((_ac) => (_ac.types.indexOf('administrative_area_level_1') >= 0)) ?? {};
	const streetNumberObject = address.address_components.find((_ac) => (_ac.types.indexOf('street_number') >= 0)) ?? {};
	const aa1Object = address.address_components.find((_ac) => (_ac.types.indexOf('administrative_area_level_1') >= 0)) ?? {};
	const aa2Object = address.address_components.find((_ac) => (_ac.types.indexOf('administrative_area_level_2') >= 0)) ?? {};
	const aa3Object = address.address_components.find((_ac) => (_ac.types.indexOf('administrative_area_level_3') >= 0)) ?? {};

	let street = streetObject.long_name;
	let city = cityObject.long_name;
	let postalCode = postalCodeObject.long_name;
	let country = countryObject.long_name;
	let state = stateObject.long_name;
	const streetNumber = streetNumberObject.long_name;

	if (!Object.keys(street ?? {}).length) {
		const premise = address.address_components.find((_ac) => (_ac.types.indexOf('premise') >= 0)) ?? {};
		const pointOfInterest = address.address_components.find((_ac) => (_ac.types.indexOf('point_of_interest') >= 0)) ?? {};
		const establishment = address.address_components.find((_ac) => (_ac.types.indexOf('establishment') >= 0)) ?? {};

		const secondaryStreet = premise?.long_name ?? pointOfInterest?.long_name ?? establishment?.long_name;
		if (secondaryStreet) {
			street = secondaryStreet;
		}

		// Parse from formatted address
		const formattedAddress = _parseFormattedAddress(address.formatted_address);

		if (formattedAddress) {
			street = street ?? formattedAddress.street;
			city = city ?? formattedAddress.city;
			state = state ?? formattedAddress.state;
			postalCode = postalCode ?? formattedAddress.postalCode;
			country = country ?? formattedAddress.country;

		}
	}

	return {
		formattedAddress: address.formatted_address,
		city,
		neighborhood: neighborhoodObject.long_name,
		postalCode,
		country,
		state,
		street,
		streetNumber,
		aa1: aa1Object.long_name,
		aa2: aa2Object.long_name,
		aa3: aa3Object.long_name,
	};
}
